import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, CheckCircle2, ChevronLeft, Copy } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { VEXU_NUMBER } from '../../data/mobileForwarding';

// Small building blocks shared by the mobile activation screens so every step
// keeps the same Vexu look: back button, heading, selectable cards, progress.

export function StepScreen({
  stepKey,
  onBack,
  children,
  footer
}: {
  stepKey: string;
  onBack?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const {
    isLight
  } = useTheme();
  return <motion.div key={stepKey} initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.22,
    ease: [0.23, 1, 0.32, 1]
  }} className="absolute inset-0 flex flex-col" data-id="element-93" data-mp="kvqvtx1ojpn1">
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-6" data-id="element-94" data-mp="1ibwanvm4hdbj">
        {onBack && <button type="button" onClick={onBack} aria-label="Back" className={`mb-5 rounded-full p-2 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-95" data-mp="28ca6vv8nfoyx">
            <ChevronLeft className="h-5 w-5" data-id="element-96" data-mp="2h5m5sjthgfgd" />
          </button>}
        {children}
      </div>
      {footer && <div className={`flex-shrink-0 space-y-3 border-t px-6 pb-8 pt-4 ${isLight ? 'border-slate-200/70 bg-stone-100' : 'border-white/5 bg-black'}`} data-id="element-97" data-mp="2ccay6ggujune">
          {footer}
        </div>}
    </motion.div>;
}
export function ScreenHeading({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) {
  const {
    isLight
  } = useTheme();
  return <div className="mb-6" data-id="element-98" data-mp="2w2wg3pue3fai">
      <h1 className={`mb-2 text-2xl font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-99" data-mp="2w2mltq2balz0">
        {title}
      </h1>
      {subtitle && <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-100" data-mp="11ovgh2r0mo9d">
          {subtitle}
        </p>}
    </div>;
}
export function SelectCard({
  selected,
  onClick,
  icon: Icon,
  title,
  desc,
  badge,
  disabledReason
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
  title: string;
  desc: string;
  badge?: string;
  /** When set, the card is shown unavailable with this explanation. */
  disabledReason?: string;
}) {
  const {
    isLight
  } = useTheme();
  const disabled = Boolean(disabledReason);
  return <button type="button" role="radio" aria-checked={selected} disabled={disabled} onClick={onClick} className={`relative w-full rounded-2xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${disabled ? isLight ? 'cursor-not-allowed border-slate-200 bg-slate-50' : 'cursor-not-allowed border-white/5 bg-white/[0.02]' : selected ? isLight ? 'border-vexu bg-white shadow-sm shadow-vexu/10' : 'border-vexu bg-vexu/10' : isLight ? 'border-slate-200 bg-white hover:border-slate-300' : 'border-white/10 bg-white/5 hover:border-white/20'}`} data-id="element-101" data-mp="3ophg02biulxl">
      <div className="flex items-start gap-3" data-id="element-102" data-mp="1ru69ktnk4p3a">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${disabled ? isLight ? 'bg-slate-100 text-slate-300' : 'bg-white/5 text-zinc-600' : selected ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/20 text-vexu' : isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-103" data-mp="1ms7ahvyuoec5">
          <Icon className="h-5 w-5" data-id="element-104" data-mp="2aknzyrkx38lh" />
        </span>
        <span className="min-w-0 flex-1 pr-7" data-id="element-105" data-mp="25qtaf7imrzeh">
          <span className="flex flex-wrap items-center gap-2" data-id="element-106" data-mp="25pi6n7p6qrnd">
            <span className={`text-[15px] font-bold ${disabled ? isLight ? 'text-slate-400' : 'text-zinc-600' : isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-107" data-mp="20rytyc84ymxj">
              {title}
            </span>
            {badge && !disabled && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-108" data-mp="2dywbm83tl9ko">
                {badge}
              </span>}
          </span>
          <span className={`mt-1 block text-[13px] leading-relaxed ${disabled ? isLight ? 'text-slate-400' : 'text-zinc-600' : isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-109" data-mp="2432qi0c1c7ra">
            {disabledReason ?? desc}
          </span>
        </span>
      </div>
      {!disabled && <span className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${selected ? 'border-vexu bg-vexu' : isLight ? 'border-slate-300 bg-white' : 'border-white/20'}`} data-id="element-110" data-mp="31r33atylosdr">
          {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} data-id="element-111" data-mp="3fnqcbv3tv901" />}
        </span>}
    </button>;
}
export function Segments({
  total,
  current
}: {
  total: number;
  current: number;
}) {
  const {
    isLight
  } = useTheme();
  return <div className="mb-6" data-id="element-112" data-mp="18yelldc0g0cf">
      <div className="flex items-center gap-2" data-id="element-113" data-mp="141fwj977ejl3">
        {Array.from({
        length: total
      }).map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= current ? 'bg-vexu' : isLight ? 'bg-slate-200' : 'bg-white/10'}`} data-id="element-114" data-mp="1hskwkcehhdvy" data-mpi={i} />)}
      </div>
      <p className={`mt-2 text-xs font-medium ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-115" data-mp="q151rv2rwkun">
        Step {current + 1} of {total}
      </p>
    </div>;
}
export function VexuNumberCard() {
  const {
    isLight
  } = useTheme();
  const [copied, setCopied] = useState(false);
  return <div className={`mb-5 flex items-center justify-between gap-3 rounded-2xl border p-4 ${isLight ? 'border-vexu/25 bg-vexu/[0.06]' : 'border-vexu/25 bg-vexu/10'}`} data-id="element-116" data-mp="u2qijrifhja4">
      <div className="min-w-0" data-id="element-117" data-mp="25if5wjzlgkbg">
        <p className={`text-[11px] font-bold uppercase tracking-wider ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-118" data-mp="25hdlhowoulh2">
          Your Vexu number
        </p>
        <p className={`mt-0.5 whitespace-nowrap text-lg font-bold tabular-nums tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-119" data-mp="183u056cfd6q7">
          {VEXU_NUMBER}
        </p>
      </div>
      <button type="button" onClick={() => {
      navigator.clipboard?.writeText(VEXU_NUMBER).catch(() => undefined);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }} aria-label={copied ? 'Number copied' : 'Copy number'} className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-bold transition-colors ${copied ? 'bg-vexu text-white' : isLight ? 'border border-slate-200 bg-white text-slate-700' : 'bg-white/10 text-white'}`} data-id="element-120" data-mp="1h5a9rbvf14td">
        {copied ? <CheckCircle2 className="h-3.5 w-3.5" data-id="element-121" data-mp="5pln6lm2q7t3" /> : <Copy className="h-3.5 w-3.5" data-id="element-122" data-mp="5ojz9gjylqty" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>;
}
export function NumberedSteps({
  steps
}: {
  steps: React.ReactNode[];
}) {
  const {
    isLight
  } = useTheme();
  return <ol className={`mb-5 space-y-3.5 rounded-2xl border p-5 ${isLight ? 'border-slate-200 bg-white shadow-sm' : 'border-white/10 bg-white/5'}`} data-id="element-123" data-mp="3pcx2w6k2b6yf">
      {steps.map((s, i) => <li key={i} className="flex items-start gap-3" data-id="element-124" data-mp="3gaxkb9tefutd" data-mpi={i}>
          <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-125" data-mp="3bcci54ul712a">
            {i + 1}
          </span>
          <span className={`pt-0.5 text-sm leading-relaxed ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-126" data-mp="31n2o3jg6n8bj">
            {s}
          </span>
        </li>)}
    </ol>;
}
export function Note({
  icon: Icon,
  tone = 'neutral',
  children
}: {
  icon: React.ElementType;
  tone?: 'neutral' | 'amber';
  children: React.ReactNode;
}) {
  const {
    isLight
  } = useTheme();
  const styles = tone === 'amber' ? isLight ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-amber-500/20 bg-amber-500/10 text-amber-200' : isLight ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-white/10 bg-white/5 text-zinc-400';
  return <div className={`mb-4 flex items-start gap-2.5 rounded-xl border p-3.5 ${styles}`} data-id="element-127" data-mp="adfmifbsazhx">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" data-id="element-128" data-mp="3srnht3o1omf1" />
      <div className="text-[13px] leading-relaxed" data-id="element-129" data-mp="1f2j6ipwr1vi">{children}</div>
    </div>;
}

/** Quiet text-style action used for tertiary options ("Contact support"). */
export function TextAction({
  onClick,
  children
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  const {
    isLight
  } = useTheme();
  return <button type="button" onClick={onClick} className={`w-full py-1 text-sm font-medium ${isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-130" data-mp="39vf6pj8cphbt">
      {children}
    </button>;
}